import { Pencil } from 'lucide-react';
import { cn } from '@/utils/cn';
import { phraseVisibilityLabel } from '@/utils/journalPhrases';
import type { JournalPhraseRow } from '@/hooks/useJournalPhrases';

export function JournalPhraseOption({
  phrase,
  active,
  canEdit,
  onHover,
  onInsert,
  onEdit,
  sharedLabel,
  privateLabel,
  editLabel,
}: {
  phrase: JournalPhraseRow;
  active: boolean;
  canEdit: boolean;
  onHover: () => void;
  onInsert: () => void;
  onEdit: () => void;
  sharedLabel: string;
  privateLabel: string;
  editLabel: string;
}) {
  const visibility = phrase.visibility === 'shared' ? 'shared' : 'private';
  const badge = phraseVisibilityLabel(visibility, { private: privateLabel, shared: sharedLabel });
  return (
    <div
      role="option"
      aria-selected={active}
      className={cn(
        'flex w-full items-start gap-1 px-2 py-1.5 text-sm',
        active ? 'bg-ds-teal-tint' : 'hover:bg-ds-row-hover',
      )}
      onMouseEnter={onHover}
    >
      <button
        type="button"
        className="min-w-0 flex-1 px-1 text-left"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onInsert}
      >
        <span className="font-medium text-ds-text">/{phrase.shortcut}</span>
        {phrase.title && (
          <span className="ml-1.5 text-ds-muted">{phrase.title}</span>
        )}
        <span className="mt-0.5 block truncate text-xs text-ds-muted">
          {phrase.body.split('\n')[0]}
        </span>
      </button>
      {canEdit && (
        <button
          type="button"
          className="mt-0.5 shrink-0 rounded p-1 text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text"
          aria-label={editLabel}
          title={editLabel}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      <span
        className={cn(
          'mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
          visibility === 'shared'
            ? 'bg-ds-teal-tint text-ds-teal'
            : 'bg-ds-neutral-tint text-ds-muted',
        )}
      >
        {badge}
      </span>
    </div>
  );
}
